import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Clock, Video, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';

const ClassCalendar = ({ courseSlug, onBack }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/special-courses/${courseSlug}/class-sessions`)
      .then(res => setSessions(res.data.sessions || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [courseSlug]);

  const today = new Date().toISOString().split('T')[0];

  const isPast = (dateStr) => dateStr && dateStr < today;
  const isToday = (dateStr) => dateStr === today;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const day = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
      return day.charAt(0).toUpperCase() + day.slice(1);
    } catch { return dateStr; }
  };

  const nextSession = sessions.find(s => !isPast(s.session_date) && !s.is_completed);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4A84B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="class-calendar-section">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4A84B] mb-6 transition-colors" data-testid="calendar-back-btn">
        <ArrowLeft className="w-4 h-4" />
        Volver al curso
      </button>

      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">Calendario de Clases</h1>
        <div className="h-0.5 w-16 bg-[#D4A84B] mt-2" />
        <p className="text-sm text-gray-400 mt-2">{sessions.length} {sessions.length === 1 ? 'sesión programada' : 'sesiones programadas'}</p>
      </div>

      {sessions.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-7 h-7 text-gray-300" />
          </div>
          <p className="text-gray-400">No hay clases programadas todavía</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="session-list">
          {sessions.map(session => {
            const past = isPast(session.session_date) || session.is_completed;
            const todayClass = isToday(session.session_date) && !session.is_completed;
            const isNext = nextSession?.id === session.id;

            return (
              <div
                key={session.id}
                className={`relative rounded-xl border-2 p-5 transition-all ${
                  todayClass
                    ? 'bg-[#FBF8F1] border-[#D4A84B] shadow-md shadow-[#D4A84B]/10'
                    : isNext
                      ? 'bg-[#FDFBF7] border-[#D4A84B]/40'
                      : past
                        ? 'bg-gray-50/50 border-gray-200 opacity-60'
                        : 'bg-white border-gray-100 hover:border-[#D4A84B]/30'
                }`}
                data-testid={`session-${session.id}`}
              >
                {/* Today badge */}
                {todayClass && (
                  <span className="absolute -top-2.5 left-4 bg-[#D4A84B] text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                    Hoy
                  </span>
                )}
                {isNext && !todayClass && (
                  <span className="absolute -top-2.5 left-4 bg-[#D4A84B]/20 text-[#D4A84B] text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                    Próxima
                  </span>
                )}

                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className={`font-heading text-base ${past ? 'text-gray-400' : 'text-[hsl(var(--text-primary))]'}`}>
                      {session.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                      <span className={`flex items-center gap-1.5 ${past ? 'text-gray-300' : 'text-gray-500'}`}>
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(session.session_date)}
                      </span>
                      {(session.start_time || session.end_time) && (
                        <span className={`flex items-center gap-1.5 ${past ? 'text-gray-300' : 'text-gray-500'}`}>
                          <Clock className="w-3.5 h-3.5" />
                          {session.start_time}{session.end_time ? ` - ${session.end_time}` : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Meet button */}
                  {!past && session.meet_url && (
                    <a
                      href={session.meet_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0"
                      data-testid={`join-session-${session.id}`}
                    >
                      <Button className="bg-[#D4A84B] hover:bg-[#c49b3f] text-white rounded-xl gap-2 shadow-sm">
                        <Video className="w-4 h-4" />
                        Unirse a la clase
                      </Button>
                    </a>
                  )}
                  {past && (
                    <span className="text-xs text-gray-300 flex-shrink-0 mt-1">Realizada</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassCalendar;
